import { useState, useEffect } from 'react';
import './AdminDashboard.css';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return {
    'Authorization': `Bearer ${token}`
  };
};

const ArticleManager = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: 'Admin',
    category: 'general',
    featuredImage: '',
    status: 'draft'
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchArticles();
  }, [filter]);

  const fetchArticles = async () => {
    try {
      const url = filter === 'all' 
        ? 'http://localhost:3001/api/articles'
        : `http://localhost:3001/api/articles?status=${filter}`;
      const response = await fetch(url);
      const data = await response.json();
      setArticles(data);
    } catch (err) {
      showMessage('error', 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const url = editingArticle
      ? `http://localhost:3001/api/articles/${editingArticle.id}`
      : 'http://localhost:3001/api/articles';

    const method = editingArticle ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showMessage('success', editingArticle ? 'Article updated' : 'Article created');
        resetForm();
        fetchArticles();
      } else {
        const error = await response.json();
        showMessage('error', error.error || 'Operation failed');
      }
    } catch (err) {
      showMessage('error', 'Failed to save article');
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      author: article.author,
      category: article.category,
      featuredImage: article.featuredImage || '',
      status: article.status
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      const response = await fetch(`http://localhost:3001/api/articles/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showMessage('success', 'Article deleted');
        fetchArticles();
      } else {
        showMessage('error', 'Delete failed');
      }
    } catch (err) {
      showMessage('error', 'Failed to delete article');
    }
  };

  const toggleStatus = async (article) => {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    try {
      const response = await fetch(`http://localhost:3001/api/articles/${article.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        showMessage('success', `Article ${newStatus}`);
        fetchArticles();
      }
    } catch (err) {
      showMessage('error', 'Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      author: 'Admin',
      category: 'general',
      featuredImage: '',
      status: 'draft'
    });
    setEditingArticle(null);
    setShowForm(false);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  if (loading) return <div className="loading">Loading articles...</div>;

  return (
    <div className="article-manager">
      <div className="manager-header">
        <h2>News & Articles Manager</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancel' : '+ New Article'}
        </button>
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {/* Article Form */}
      {showForm && (
        <div className="article-form">
          <h3>{editingArticle ? 'Edit Article' : 'Create New Article'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Article title"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Author</label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="Author name"
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="general">General</option>
                  <option value="news">News</option>
                  <option value="events">Events</option>
                  <option value="announcements">Announcements</option>
                  <option value="community">Community</option>
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Featured Image URL</label>
              <input
                type="text"
                value={formData.featuredImage}
                onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                placeholder="https://example.com/image.jpg or /uploads/gallery/image.jpg"
              />
            </div>

            <div className="form-group">
              <label>Content *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Article content..."
                rows="10"
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingArticle ? 'Update Article' : 'Create Article'}
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All ({articles.length})
        </button>
        <button
          className={filter === 'published' ? 'active' : ''}
          onClick={() => setFilter('published')}
        >
          Published
        </button>
        <button
          className={filter === 'draft' ? 'active' : ''}
          onClick={() => setFilter('draft')}
        >
          Drafts
        </button>
      </div>

      {/* Articles List */}
      <div className="articles-list">
        {articles.map((article) => (
          <div key={article.id} className="article-card">
            <div className="article-header">
              <h3>{article.title}</h3>
              <span className={`status-badge ${article.status}`}>
                {article.status}
              </span>
            </div>
            <div className="article-meta">
              <span>By {article.author}</span>
              <span>•</span>
              <span>{article.category}</span>
              <span>•</span>
              <span>{new Date(article.createdAt).toLocaleDateString()}</span>
            </div>
            <p className="article-excerpt">
              {article.content.substring(0, 150)}...
            </p>
            <div className="article-actions">
              <button onClick={() => handleEdit(article)} className="btn-edit">
                Edit
              </button>
              <button onClick={() => toggleStatus(article)} className="btn-toggle">
                {article.status === 'published' ? 'Unpublish' : 'Publish'}
              </button>
              <button onClick={() => handleDelete(article.id)} className="btn-delete">
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {articles.length === 0 && (
        <p className="no-data">
          No articles found. {!showForm && 'Click "New Article" to create one!'}
        </p>
      )}
    </div>
  );
};

export default ArticleManager;
